import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  TableContainer, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField, useMediaQuery, Card, CardContent, Typography, Grid,
  Select, MenuItem, FormControl, InputLabel, Box, Stack,
} from '@mui/material';

import { useTheme } from '@mui/material/styles';

function AdminCompanyEdit() {

  const [company, setCompany] = useState([]);
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  
  const [formData, setFormData] = useState({
    ID: '',
    Company_Name: '',
    Company_Password: '',
    Company_FullName: '',
    Company_Mail: '',
    Company_FoundDate: '',
    role : ''

  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // Responsive handling for dialog
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'lg')); // Tablet view

  // "Sıra No" is a display-only sequential index (1..N) over the alphabetical
  // order — it has nothing to do with the DB primary key (Company.ID stays
  // stable so every related row keeps pointing at the right firm). Sorting
  // here (not just relying on the API order) keeps numbering correct right
  // after a firm is added locally, before the next refresh.
  const sortedCompanies = useMemo(
    () => [...(company || [])].sort(
      (a, b) => (a.Company_Name || '').localeCompare(b.Company_Name || '', 'tr'),
    ),
    [company],
  );

  useEffect(() => {    
    axios
      .get('https://portal.peaksoftcon.com:8081/api/companies')
      .then((response) => setCompany(response.data))
      .catch((error) => console.error('Error fetching data:', error));
      
  }, []);

  // HTML <input type="date"> wants YYYY-MM-DD. MySQL DATE fields come back as
  // Date objects from the `mysql` driver, or ISO strings — normalize either.
  const toDateInputValue = (v) => {
    if (!v) return '';
    if (typeof v === 'string') return v.slice(0, 10);
    if (v instanceof Date) return v.toISOString().slice(0, 10);
    return '';
  };

  const handleOpen = (company) => {
    if (company) {
      setIsEditing(true);
      setFormData({
        ...company,
        Company_FoundDate: toDateInputValue(company.Company_FoundDate),
      });
    } else {
      setIsEditing(false);
      setFormData({
        ID: '', Company_FullName: '', Company_Mail: '', Company_FoundDate: '',
        monthly_fee: '', billing_day: 1,
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleSave = () => {
    if (isEditing) {
        // Update company via API
        axios.put(`https://portal.peaksoftcon.com:8081/api/companyUpdate/${formData.ID}`, formData)
          .then(() => {
            setCompany(prev => prev.map(t => (t.ID === formData.ID ? formData : t)));
            handleClose();
          })
          .catch(error => console.error('Error updating company:', error));
      } else {
        // Add new company via API
        axios.post('https://portal.peaksoftcon.com:8081/api/companyInsert', formData)
          .then((response) => {
            setCompany(prev => [...prev, { ...formData, ID: response.data.ID }]);
            handleClose();
          })
          .catch(error => console.error('Error adding new company:', error));
      }
  };

  const handleDelete = (id) => {
    axios.delete(`https://portal.peaksoftcon.com:8081/api/companyDelete/${id}`)
    .then(() => {
        setCompany(prev => prev.filter(company => company.ID !== id));
        console.log("Company deleted successfully");
    })
    .catch(error => console.error('Error deleting company:', error));
   
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }} sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ flexGrow: 1 }}>Kayıtlı Firmalar</Typography>
        <Button variant="contained" color="primary" onClick={() => handleOpen()}>
          + Yeni Firma Ekle
        </Button>
      </Stack>
      {!isMobile && !isTablet ? (
        // Desktop View - Table Format
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sıra No</TableCell>
                <TableCell>Firma İsmi</TableCell>
                <TableCell>Firma Ünvanı</TableCell>
                <TableCell>Vergi Dairesi</TableCell>
                <TableCell>Vergi No</TableCell>
                <TableCell>Firma Mail</TableCell>
                <TableCell>Kuruluş Tarihi</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedCompanies.map((company, index) => (
                <TableRow key={company.ID}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>{company.Company_Name}</TableCell>
                  <TableCell>{company.Company_FullName}</TableCell>
                  <TableCell>{company.Company_TaxOffice}</TableCell>
                  <TableCell>{company.Company_TaxNumber}</TableCell>
                  <TableCell>{company.Company_Mail}</TableCell>
                  <TableCell>{toDateInputValue(company.Company_FoundDate)}</TableCell>
                  <TableCell>
                    <Button variant="outlined" color="primary" onClick={() => handleOpen(company)}>
                      Düzenle
                    </Button>
                    {company.role!=='Admin' &&(
                    <Button variant="outlined" color="secondary" onClick={() => handleDelete(company.ID)} style={{ margin: '8px' }}>
                      Sil
                    </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        // Tablet and Mobile View - Card Format with Adaptive Columns
        <Grid container spacing={2}>
          {sortedCompanies.map((company, index) => (
            <Grid item xs={12} sm={6} md={4} key={company.ID}> {/* Stacks on mobile, 2 columns on tablet, 3 on larger screens */}
              <Card style={{ marginBottom: '16px' }}>
                <CardContent>
                  <Typography variant="h6">Sıra No: {index + 1}</Typography>
                  <Typography>Firma İsmi: {company.Company_Name}</Typography>
                  <Typography>Firma Ünvanı: {company.Company_FullName}</Typography>
                  <Typography>Vergi Dairesi: {company.Company_TaxOffice}</Typography>
                  <Typography>Vergi No: {company.Company_TaxNumber}</Typography>
                  <Typography>Firma Mail: {company.Company_Mail}</Typography>
                  <Typography>Kuruluş Tarihi: {toDateInputValue(company.Company_FoundDate)}</Typography>
                  <div style={{ marginTop: '8px' }}>
                    <Button variant="outlined" color="primary" onClick={() => handleOpen(company)}>
                      Düzenle
                    </Button>
                    {company.role!=='Admin' &&(
                    <Button variant="outlined" color="error" onClick={() => handleDelete(company.ID)} style={{ marginLeft: '8px' }}>
                      Sil
                    </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      <Dialog open={open} onClose={handleClose} fullScreen={isMobile} maxWidth="md" fullWidth>
        <DialogTitle>{isEditing ? "Firmayı Düzenle" : "Yeni Firma Ekle"}</DialogTitle>
        <DialogContent>
          {[
            { name: 'Company_Name',      label: 'Firma İsmi' },
            { name: 'Company_Password',  label: 'Şifre', type: 'password' },
            { name: 'Company_FullName',  label: 'Firma Ünvanı' },
            { name: 'Company_TaxOffice', label: 'Vergi Dairesi' },
            { name: 'Company_TaxNumber', label: 'Vergi No' },
            { name: 'Company_Mail',      label: 'E-posta', type: 'email' },
            { name: 'Company_FoundDate', label: 'Kuruluş Tarihi', type: 'date' },
          ].map((f) => (
            <TextField
              key={f.name}
              label={f.label}
              name={f.name}
              type={f.type || 'text'}
              value={formData[f.name] || ''}
              onChange={handleChange}
              fullWidth
              margin="dense"
              InputLabelProps={f.type === 'date' ? { shrink: true } : undefined}
            />
          ))}
          <FormControl fullWidth margin="dense">
            <InputLabel id="role-label">Rol</InputLabel>
            <Select
              labelId="role-label"
              label="Rol"
              name="role"
              value={formData.role || ''}
              onChange={handleChange}
            >
              <MenuItem value="Admin">Admin</MenuItem>
              <MenuItem value="Client">Client</MenuItem>
            </Select>
          </FormControl>
          {formData.role !== 'Admin' && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Aylık Tutar (₺)"
                name="monthly_fee"
                type="number"
                inputProps={{ step: '0.01', min: 0 }}
                value={formData.monthly_fee ?? ''}
                onChange={handleChange}
                fullWidth
                margin="dense"
              />
              <FormControl fullWidth margin="dense">
                <InputLabel id="billing-day-label">Kesim Günü</InputLabel>
                <Select
                  labelId="billing-day-label"
                  label="Kesim Günü"
                  name="billing_day"
                  value={formData.billing_day ?? 1}
                  onChange={handleChange}
                >
                  <MenuItem value={1}>Ayın 1'i</MenuItem>
                  <MenuItem value={15}>Ayın 15'i</MenuItem>
                </Select>
              </FormControl>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary">
            İptal
          </Button>
          <Button onClick={handleSave} color="primary">
            Kaydet
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminCompanyEdit;
